import { useEffect, useId, useLayoutEffect, useState, type JSX, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { EDGE_GAP, outside, useAnchoredPanel } from './anchored';
import { Icon } from './Icon';

export interface MultiSelectOption {
  value: string;
  label: string;
  /**
   * Почему выбрать нельзя.
   *
   * Пункт при этом не исчезает: спрятанный он выглядел бы несуществующим,
   * и человек искал бы его в другом месте. Он выключен, а причина —
   * в подсказке при наведении.
   */
  disabledReason?: string | undefined;
}

/**
 * Выбор нескольких значений — список с галочками под кнопкой.
 *
 * Заведён вместо коробки с галочками, врисованной прямо в строку формы.
 * Коробка занимала место постоянно и по высоте не совпадала ни с одним
 * соседним полем: строка формы выравнивается по верхнему краю, и поле
 * в 34 px стояло рядом со списком в 120 px, а кнопки уезжали к нижнему краю
 * этого списка. Кнопка ростом с поле ввода такой разницы не создаёт,
 * а список раскрывается только тогда, когда в него смотрят.
 *
 * Панель рисуется порталом в `body` — по той же причине, что у `InfoNote`:
 * формы живут внутри контейнеров с `overflow-hidden`, и раскрытый список
 * обрезался бы их краем, а не вылезал за него.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  placeholder,
  hint,
  empty,
  disabled = false,
}: {
  label: string;
  options: MultiSelectOption[];
  /** Выбранные значения. Значения, которых нет среди пунктов, не показываются. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Что стоит в кнопке, пока ничего не выбрано. */
  placeholder: string;
  hint?: ReactNode;
  /** Что написать в пустом списке. Без него пустая панель молчит. */
  empty?: string;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const id = useId();
  const panelId = `${id}-panel`;

  const { anchor, panel, style } = useAnchoredPanel<HTMLButtonElement, HTMLDivElement>(open);

  /*
    Ширина панели равна ширине кнопки: список, вылезающий из-под своей
    кнопки на треть, читается как чужой. Меряется заранее, а не при
    раскрытии: `useAnchoredPanel` считает положение по уже отрисованной
    панели, и ширина, приехавшая следующим кадром, оставила бы её
    выровненной по прежнему размеру.
  */
  const [anchorWidth, setAnchorWidth] = useState(0);

  useLayoutEffect(() => {
    const measure = (): void => setAnchorWidth(anchor.current?.getBoundingClientRect().width ?? 0);

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [anchor]);

  // Закрытие по Escape и по нажатию мимо — тот же порядок, что у пояснения
  // и у шторки: закрывать список должно всё, что означает «выбрал».
  useEffect(() => {
    if (!open) {
      return;
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        anchor.current?.focus();
      }
    };

    const onOutside = (event: PointerEvent): void => {
      if (outside(event.target, anchor, panel)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onOutside, true);

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onOutside, true);
    };
  }, [open, anchor, panel]);

  /*
    Фокус переезжает в панель. Она нарисована в конце `body`, а не рядом
    с кнопкой, поэтому табуляция от кнопки уходила бы к следующему полю
    формы мимо списка — с клавиатуры он был бы недостижим вовсе.
  */
  useEffect(() => {
    if (open) {
      panel.current?.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
    }
  }, [open, panel]);

  const chosen = options.filter((option) => value.includes(option.value));

  function toggle(option: string): void {
    onChange(
      value.includes(option) ? value.filter((current) => current !== option) : [...value, option],
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {/*
        Подпись не `<label>`: доступным именем кнопки служит подпись вместе
        с перечнем выбранного (`aria-labelledby`), и обёртка тут не поможет —
        она прочитала бы перечень дважды.
      */}
      <span className="text-sm text-muted" id={`${id}-label`}>
        {label}
      </span>

      <button
        type="button"
        ref={anchor}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-labelledby={`${id}-label ${id}-summary`}
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
        onClick={() => setOpen((current) => !current)}
        className={[
          'flex h-[34px] w-full items-center gap-2 rounded-sm border border-line-strong',
          'bg-raised px-2.5 text-left text-base text-fg focus:outline-none',
          disabled ? 'text-dim' : '',
        ].join(' ')}
      >
        <span
          id={`${id}-summary`}
          className={`flex-1 truncate ${chosen.length === 0 ? 'text-faint' : ''}`}
        >
          {chosen.length === 0 ? placeholder : chosen.map((option) => option.label).join(', ')}
        </span>

        {/* Счётчик — чтобы «и ещё две» было видно, когда перечень не влез. */}
        {chosen.length > 1 ? (
          <span className="flex-none font-mono text-xs text-dim">{chosen.length}</span>
        ) : null}

        <Icon name="chevronDown" />
      </button>

      {hint === undefined ? null : (
        <span id={`${id}-hint`} className="font-mono text-xs text-dim">
          {hint}
        </span>
      )}

      {open
        ? createPortal(
            <div
              id={panelId}
              ref={panel}
              role="group"
              aria-labelledby={`${id}-label`}
              /*
                Фокус, ушедший из панели по табуляции, её закрывает. Нажатие
                мимо ловится отдельно (`pointerdown`): у него `relatedTarget`
                пуст, и по нему одному не отличить уход на соседнее поле
                от щелчка по полосе прокрутки самой панели.
              */
              onBlur={(event) => {
                if (event.relatedTarget !== null && outside(event.relatedTarget, anchor, panel)) {
                  setOpen(false);
                }
              }}
              style={{ ...style, minWidth: anchorWidth, maxWidth: panelMaxWidth() }}
              className="z-50 max-h-[240px] overflow-auto rounded-sm border border-line-strong bg-raised py-1 shadow-lg"
            >
              {options.length === 0 ? (
                empty === undefined ? null : (
                  <p className="px-2.5 py-1 text-sm text-dim">{empty}</p>
                )
              ) : (
                options.map((option) => (
                  <label
                    key={option.value}
                    title={option.disabledReason}
                    className={`flex items-center gap-2 px-2.5 py-1 text-sm ${
                      option.disabledReason === undefined ? 'hover:bg-row-alt' : 'text-dim'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      disabled={option.disabledReason !== undefined}
                      onChange={() => toggle(option.value)}
                    />
                    <span className="truncate">{option.label}</span>
                  </label>
                ))
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * Предел ширины панели.
 *
 * Меньшее из «столько, чтобы длинное название площадки читалось» и ширины
 * экрана с полями. Нижнюю границу задаёт кнопка через `minWidth`, и она
 * в CSS сильнее: панель уже своей кнопки не станет.
 */
function panelMaxWidth(): number {
  const room = typeof window === 'undefined' ? 420 : window.innerWidth - EDGE_GAP * 2;

  return Math.min(420, room);
}
